import { Map, List, getIn } from 'immutable';
import { createSelector } from 'reselect';

export * from './list/selectors';
export * from './marketplace/selectors';
export * from './networkSignup/selectors';
export * from './openIdSignup/selectors';
export * from './signup/selectors';

const selectMe = (state) => state.get('me');
const selectUserFeatures = createSelector(selectMe, (state) =>
  state.getIn(['user', 'membership', 'features']),
);
const selectFeatureFromProps = (_, props) => props.flag;
const selectFundingSearchIdFromUrl = (_, props) =>
  parseInt(getIn(props, ['match', 'params', 'fundingSearchId'], ''), 10);

const makeSelectMatchesResultsData = () =>
  createSelector(selectMe, (state) =>
    state.getIn(['marketplace', 'questionnairePrefill', 'network']),
  );

const makeSelectLoading = () =>
  createSelector(selectMe, (state) => state.getIn(['network', 'loading']));

const makeSelectHasUser = () => createSelector(selectMe, (state) => state.get('user') !== false);

const makeSelectUser = () => createSelector(selectMe, (state) => state.get('user'));

const makeSelectUserId = () => createSelector(selectMe, (state) => state.getIn(['user', 'id']));

const makeSelectUserType = () =>
  createSelector(selectMe, (state) => state.getIn(['user', 'userType']));

  const makeSelectUserActive = () =>
  createSelector(selectMe, (state) => state.getIn(['user', 'company', 'entityStatus']));

const makeSelectUserIntroducerToken = () =>
  createSelector(selectMe, (state) => state.getIn(['user', 'introducerToken']));

const makeSelectUserEmail = () =>
  createSelector(selectMe, (state) => state.getIn(['user', 'email']));

const makeSelectUserName = () => createSelector(selectMe, (state) => state.getIn(['user', 'name']));

const makeSelectHasNetworkManagerRole = () =>
  createSelector(selectMe, (state) => state.getIn(['user', 'hasNetworkManagerRole']));

const makeSelectUserLevel = () =>
  createSelector(selectMe, (state) => state.getIn(['user', 'membership', 'level'], Map()));

const makeSelectUserPlan = () =>
  createSelector(selectMe, (state) => state.getIn(['user', 'membership', 'level', 'name']));

const makeSelectUserClientsLimit = () =>
  createSelector(selectMe, (state) =>
    state.getIn(['user', 'membership', 'features', 'clients_limit']),
  );

const makeSelectUserFeature = (feature, allowUndefined = false) =>
  createSelector(
    selectUserFeatures,
    (state) => state?.get(feature, allowUndefined) ?? allowUndefined,
  );

const makeSelectFeatureActiveFromProps = () =>
  createSelector(selectFeatureFromProps, selectUserFeatures, (flagName, state) =>
    !flagName || !state ? false : state.get(flagName, false),
  );

const makeSelectUserHasFundingSearches = () =>
  createSelector(selectMe, (state) => state.getIn(['user', 'hasFundingSearches'], false));

const makeSelectUserFundingSearchCount = () =>
  createSelector(selectMe, (state) => state.getIn(['user', 'fundingSearchCount'], 0));

const makeSelectProfile = () =>
  createSelector(selectMe, (state) => state.getIn(['user', 'profile']));

const makeSelectPreferences = () =>
  createSelector(selectMe, (state) => state.getIn(['user', 'profile', 'preferences'], Map()));

const makeSelectUserNetwork = () =>
  createSelector(selectMe, (state) => state.getIn(['user', 'network'], Map()));

const makeSelectUserNetworkName = () =>
  createSelector(makeSelectUserNetwork(), (state) => state.get('name'));

const makeSelectHasFundingSearches = () =>
  createSelector(selectMe, (state) => state.getIn(['user', 'hasFundingSearches']));

const makeSelectUserCompany = () =>
  createSelector(selectMe, (state) => state.getIn(['user', 'company'], Map()));

const makeSelectUserCompanyName = () =>
  createSelector(makeSelectUserCompany(), (state) => state.get('name'));

const makeSelectUserTeam = () =>
  createSelector(selectMe, (state) => state.getIn(['user', 'team'], Map()));

const makeSelectUserTeamIsDefaultTeam = () =>
  createSelector(makeSelectUserTeam(), (state) => !!state.get('default'));

const makeSelectUserTeamId = () => createSelector(makeSelectUserTeam(), (state) => state.get('id'));

const makeSelectUserTeamName = () =>
  createSelector(makeSelectUserTeam(), (state) => state.get('name'));

const makeSelectUserCompanyId = () =>
  createSelector(makeSelectUserCompany(), (state) => state.get('company_id'));

const makeSelectProfileBannerDismissed = () =>
  createSelector(
    selectFundingSearchIdFromUrl,
    makeSelectPreferences(),
    (fundingSearchId, userPreferences) => {
      const list = userPreferences.get('dismissedFundingSearchBanners', List());
      return list.includes(fundingSearchId);
    },
  );

const makeSelectUserConsultant = () =>
  createSelector(selectMe, (state) => state.getIn(['user', 'consultant'], Map()));

const makeSelectUserCompanyPrimaryUserId = () =>
  createSelector(selectMe, (state) => state.getIn(['user', 'company', 'primaryUser', 'id']));

const makeSelectUserCountryCode = () =>
  createSelector(selectMe, (state) => state.getIn(['user', 'locale', 'countryCode']));

export {
  makeSelectHasFundingSearches,
  makeSelectHasNetworkManagerRole,
  makeSelectHasUser,
  makeSelectLoading,
  makeSelectPreferences,
  makeSelectProfile,
  makeSelectProfileBannerDismissed,
  makeSelectUser,
  makeSelectUserActive,
  makeSelectUserCompany,
  makeSelectUserCompanyId,
  makeSelectUserCompanyName,
  makeSelectUserCompanyPrimaryUserId,
  makeSelectUserConsultant,
  makeSelectUserEmail,
  makeSelectUserFeature,
  makeSelectFeatureActiveFromProps,
  makeSelectUserClientsLimit,
  makeSelectUserCountryCode,
  makeSelectUserFundingSearchCount,
  makeSelectUserHasFundingSearches,
  makeSelectUserId,
  makeSelectUserIntroducerToken,
  makeSelectUserLevel,
  makeSelectUserName,
  makeSelectUserNetwork,
  makeSelectUserNetworkName,
  makeSelectUserPlan,
  makeSelectUserTeam,
  makeSelectUserTeamId,
  makeSelectUserTeamIsDefaultTeam,
  makeSelectUserTeamName,
  makeSelectUserType,
  makeSelectMatchesResultsData,
};
